import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./skillPackageFiles.html"; // ORCHESTRATOR for the Package Files tab. Composes two child components (skillFileBrowser +
// skillFileViewer) and owns data orchestration: listing prop, tree fetch, body cache,
// AbortController cancellation, stale-response guard, selection state.
// Shared between skill and plugin listings — the source + files are read from whichever
// extension matches `listing.technology`.
import axios from 'axios';
import { WireI18n } from '~/modules/stores/i18nStore';
import { isPluginListing } from '~/modules/utilities/listing_utils';
import { getPluginFiles, getPluginReadme, getPluginSource } from '~/modules/utilities/plugin_utils';
import { buildSkillFileTree, getSkillFiles, getSkillMarkdown, getSkillSource, joinRepositoryPath } from '~/modules/utilities/skill_utils';
import { fetchSkillFileBody } from '~/modules/services/skillGithubFetch';
import { CookieConsentCategory, createCatCookie, getCookieConsented } from '~/modules/utilities/cookies';

// A plugin's baked-in landing document, folder-relative — the plugin analog of a skill's SKILL.md.
const PLUGIN_README_PATH = 'README.md';

// The browser width is shared between skill and plugin package-file viewers. Keeping it as a
// percentage makes the preference useful across viewport sizes, while the runtime minimum keeps
// the controls usable and the 40% ceiling protects the primary file viewer.
const TREE_WIDTH_COOKIE_NAME = 'appx_package_files_tree_width';
const TREE_WIDTH_COOKIE_EXPIRY_DAYS = 30;
const TREE_WIDTH_DEFAULT_PERCENT = 32;
const TREE_WIDTH_MIN_PERCENT = 24;
const TREE_WIDTH_MAX_PERCENT = 40;
const TREE_WIDTH_MIN_PX = 256;
const TREE_WIDTH_KEY_STEP = 2;
const TREE_WIDTH_KEY_LARGE_STEP = 5;
function readInitialTreeWidth() {
  if (typeof window === 'undefined') {
    return TREE_WIDTH_DEFAULT_PERCENT;
  }
  const savedWidth = Number.parseFloat(getCookieConsented(TREE_WIDTH_COOKIE_NAME, CookieConsentCategory.Functional));
  return Number.isFinite(savedWidth) && savedWidth >= TREE_WIDTH_MIN_PERCENT && savedWidth <= TREE_WIDTH_MAX_PERCENT ? savedWidth : TREE_WIDTH_DEFAULT_PERCENT;
}
class SkillPackageFiles extends LightningElement {
  constructor(...args) {
    super(...args);
    this.labels = void 0;
    // The raw listing, stored privately so the @api setter can detect replacement.
    this._listing = null;
    // The skill's GitHub source coordinates, or null when absent (no skillSource = cannot fetch files).
    this.source = null;
    // The fetched file tree (nested SkillFileNode[]). Reactive; reassign the array reference to trigger LWC re-render.
    this.nodes = [];
    // Fetched file bodies, keyed by path (SkillFileNode.path). Reactive; reassign the object to trigger LWC re-render.
    this.bodies = {};
    // The currently selected file's path (full repo-relative path), or '' when no selection yet.
    // IMPORTANT: this field MUST have an initializer. Under the client tsconfig (target ES6, so
    // useDefineForClassFields defaults to false), an UNINITIALIZED class field emits no runtime code,
    // so LWC's reactivity membrane never registers it and reassigning it does NOT re-render. That made
    // a cached file un-reselectable: selectFile() sets only this field and returns early for cached
    // bodies, so with no reactivity the tree/breadcrumb froze on the previous selection. reactive
    this.selectedPath = '';
    // Paths currently being fetched (to show a spinner). Reactive; reassign the Set to trigger LWC re-render.
    this.loadingPaths = new Set();
    // Files whose most recent body request failed. Kept separate from `bodies` so an empty file is
    // a valid cached success while a failed request gets a visible retry state.
    this.failedPaths = new Set();
    // The AbortController for the in-flight body fetch, if any. When a new fetch starts, abort the previous one.
    // True when the left pane (file tree) is collapsed.
    this.treeCollapsed = false;
    // reactive
    // A stable, user-adjustable pane width. File names truncate within this width instead of
    // changing the split as users browse. The preference is shared across skills and plugins.
    this.treeWidthPercent = readInitialTreeWidth();
    // reactive
    this.isResizingTree = false;
    // reactive
    this.measuredWrapperWidth = 0;
    // reactive so ARIA values follow responsive CSS clamping
    this.shouldRestoreTreeToggleFocus = false;
    this.handleSplitterPointerMove = event => {
      this.resizeTreeToClientX(event.clientX);
    };
    this.handleSplitterPointerUp = () => {
      if (!this.isResizingTree) {
        return;
      }
      this.stopTreeResize();
      this.persistTreeWidth();
    };
  }
  get listing() {
    return this._listing;
  }

  // Setter for listing: detect when the listing changes (SPA nav between skills) and refetch the tree.
  set listing(value) {
    if (this._listing === value) {
      return;
    }
    this._listing = value;
    this.source = isPluginListing(value) ? getPluginSource(value) : getSkillSource(value);
    this.nodes = [];
    this.bodies = {};
    this.selectedPath = '';
    this.loadingPaths = new Set();
    this.failedPaths = new Set();
    this.bodyFetchController?.abort();
    this.bodyFetchController = undefined;
    this.treeCollapsed = false;
    if (this.source) {
      this.fetchTree();
    }
  }

  // Build the skill's file tree from the listing's files array (once per listing load).
  fetchTree() {
    if (!this.source) {
      return;
    }
    const isPlugin = isPluginListing(this._listing ?? undefined);
    const files = isPlugin ? getPluginFiles(this._listing) : getSkillFiles(this._listing);
    const fetchedNodes = buildSkillFileTree(files, this.source.path);
    // Reassign to trigger reactivity
    this.nodes = fetchedNodes;

    // The listing payload already carries its landing document (`skillmd` for a skill, `readme`
    // for a plugin). Pre-seed that body BEFORE default selection so neither landing view causes
    // a BFF/GitHub request, including later re-clicks. If the payload field is missing, normal
    // lazy fetching remains available as a backward-compatible fallback.
    const entryRelativePath = isPlugin ? PLUGIN_README_PATH : 'SKILL.md';
    const entryPath = joinRepositoryPath(this.source.path, entryRelativePath);
    const entryBody = isPlugin ? getPluginReadme(this._listing) : getSkillMarkdown(this._listing);
    if (entryBody) {
      this.bodies = {
        ...this.bodies,
        [entryPath]: entryBody
      };
    }

    // Default-select the listing's baked-in document — SKILL.md for a skill and README.md for a
    // plugin — else the first file node (depth-first) for legacy payloads without that file.
    if (fetchedNodes.length > 0) {
      const entryNode = this.findNodeByPath(fetchedNodes, entryPath);
      if (entryNode) {
        this.selectFile(entryPath);
      } else {
        const firstFile = this.findFirstFile(fetchedNodes);
        if (firstFile) {
          this.selectFile(firstFile.path);
        }
      }
    }
  }

  // Find a node by path in the nested tree (depth-first).
  findNodeByPath(nodes, path) {
    for (const node of nodes) {
      if (node.path === path) {
        return node;
      }
      if (node.children) {
        const found = this.findNodeByPath(node.children, path);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  // Find the first file node in the tree (depth-first).
  findFirstFile(nodes) {
    for (const node of nodes) {
      if (node.type === 'file') {
        return node;
      }
      if (node.children) {
        const found = this.findFirstFile(node.children);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  // Select a file: set selectedPath and lazily fetch its body if not cached.
  async selectFile(path) {
    // R1 (Optimistic selection): Set selectedPath IMMEDIATELY so the pane transitions right away,
    // before the fetch. The loading state will show a spinner until the body arrives.
    this.selectedPath = path;

    // If the body is already cached, show it immediately with no fetch and no spinner.
    if (Object.prototype.hasOwnProperty.call(this.bodies, path)) {
      return;
    }
    if (!this.source) {
      return;
    }

    // R2 (Cancel previous fetch): Abort the in-flight fetch, if any, to free the network and
    // ensure a stale late-arriver won't overwrite the new selection.
    this.bodyFetchController?.abort();
    const controller = new AbortController();
    this.bodyFetchController = controller;
    const signal = controller.signal;

    // Show loading state immediately (spinner in the right pane).
    this.loadingPaths = new Set(this.loadingPaths).add(path);
    const nextFailedPaths = new Set(this.failedPaths);
    nextFailedPaths.delete(path);
    this.failedPaths = nextFailedPaths;
    try {
      const listingId = this._listing?.external_id ?? '';
      // path is the FULL repo-relative path (rooted at this.source.path); the BFF now takes
      // only a folder-relative file, since it resolves the source folder itself server-side.
      const file = path.startsWith(`${this.source.path}/`) ? path.slice(this.source.path.length + 1) : path;
      const body = await fetchSkillFileBody(listingId, file, signal);

      // R3 (Guard against stale responses): After the fetch resolves, CHECK that the path we
      // fetched is still the current selectedPath. If the user has since selected a different
      // file, DROP this result (don't cache it, don't render it). Only the fetch matching the
      // current selection may update the pane.
      if (this.selectedPath !== path) {
        // Stale response: user has moved on. Don't cache the body or update the pane.
        return;
      }

      // Success: cache the body and clear the loading flag. Reassign to trigger reactivity.
      this.bodies = {
        ...this.bodies,
        [path]: body
      };
    } catch (error) {
      // Cancellation is expected when the user picks a different file. A real failure remains
      // retryable and is shown only if this is still the active selection.
      if (!axios.isCancel(error) && this.selectedPath === path) {
        this.failedPaths = new Set(this.failedPaths).add(path);
      }
    } finally {
      const isCurrentRequest = this.bodyFetchController === controller;
      const hasNewerRequestForSamePath = !isCurrentRequest && this.selectedPath === path;

      // A replacement fetch for the same path owns the same loading flag. Its predecessor's
      // abort/finally must not clear that flag while the replacement is still running.
      if (!hasNewerRequestForSamePath) {
        const newLoadingPaths = new Set(this.loadingPaths);
        newLoadingPaths.delete(path);
        this.loadingPaths = newLoadingPaths;
      }
      if (isCurrentRequest) {
        this.bodyFetchController = undefined;
      }
    }
  }

  // FileTree is the single analytics source for user file selection. This handler owns only the
  // selection/fetch behavior, preventing the former fileTreeFile + view-file double source.
  handleBrowserSelect(event) {
    const path = event.detail.path;
    this.selectFile(path);
  }

  // Handler for 'toggletree' event from either child (browser's collapse button when expanded, or
  // viewer's expand button when collapsed).
  handleToggleTree() {
    if (!this.treeCollapsed && this.isResizingTree) {
      this.stopTreeResize();
    }
    this.shouldRestoreTreeToggleFocus = true;
    this.treeCollapsed = !this.treeCollapsed;
  }
  handleSplitterPointerDown(event) {
    if (this.treeCollapsed || event.button !== 0) {
      return;
    }
    event.preventDefault();
    this.isResizingTree = true;
    this.resizeTreeToClientX(event.clientX);
    window.addEventListener('pointermove', this.handleSplitterPointerMove);
    window.addEventListener('pointerup', this.handleSplitterPointerUp);
    window.addEventListener('pointercancel', this.handleSplitterPointerUp);
  }
  handleSplitterKeyDown(event) {
    if (this.treeCollapsed) {
      return;
    }
    const wrapperWidth = this.getWrapperWidth();
    const minimum = this.getMinimumTreeWidthPercent(wrapperWidth);
    const step = event.shiftKey ? TREE_WIDTH_KEY_LARGE_STEP : TREE_WIDTH_KEY_STEP;
    let nextWidth;
    switch (event.key) {
      case 'ArrowLeft':
        nextWidth = this.treeWidthPercent - step;
        break;
      case 'ArrowRight':
        nextWidth = this.treeWidthPercent + step;
        break;
      case 'Home':
        nextWidth = minimum;
        break;
      case 'End':
        nextWidth = TREE_WIDTH_MAX_PERCENT;
        break;
      default:
        return;
    }
    event.preventDefault();
    this.treeWidthPercent = this.clampTreeWidth(nextWidth, wrapperWidth);
    this.persistTreeWidth();
  }
  renderedCallback() {
    // LWC serializes the false state of a bound boolean global attribute as `inert="false"`.
    // Since inertness is based on attribute presence, explicitly remove it after expansion.
    const fileTreePane = this.template?.querySelector('.file-tree-pane');
    if (this.treeCollapsed) {
      fileTreePane?.setAttribute('inert', '');
    } else {
      fileTreePane?.removeAttribute('inert');
    }
    const wrapper = this.template?.querySelector('.two-pane-wrapper');
    if (wrapper !== this.observedWrapper) {
      this.wrapperResizeObserver?.disconnect();
      this.observedWrapper = wrapper ?? undefined;
      if (wrapper && typeof ResizeObserver !== 'undefined') {
        this.wrapperResizeObserver = new ResizeObserver(() => this.measureWrapper(wrapper));
        this.wrapperResizeObserver.observe(wrapper);
      }
    }
    if (wrapper) {
      this.measureWrapper(wrapper);
    }
    if (this.shouldRestoreTreeToggleFocus) {
      this.shouldRestoreTreeToggleFocus = false;
      const targetSelector = this.treeCollapsed ? 'listing-skill-file-viewer' : 'listing-skill-file-browser';
      this.template?.querySelector(targetSelector)?.focusTreeToggle();
    }
  }
  disconnectedCallback() {
    this.bodyFetchController?.abort();
    this.wrapperResizeObserver?.disconnect();
    this.stopTreeResize();
  }
  measureWrapper(wrapper) {
    const width = wrapper.getBoundingClientRect().width;
    if (width > 0 && Math.abs(width - this.measuredWrapperWidth) >= 1) {
      this.measuredWrapperWidth = width;
    }
  }
  resizeTreeToClientX(clientX) {
    const wrapper = this.template?.querySelector('.two-pane-wrapper');
    if (!wrapper) {
      return;
    }
    const rect = wrapper.getBoundingClientRect();
    if (rect.width <= 0) {
      return;
    }
    const requestedPercent = (clientX - rect.left) / rect.width * 100;
    this.treeWidthPercent = this.clampTreeWidth(requestedPercent, rect.width);
  }
  getWrapperWidth() {
    return this.template?.querySelector('.two-pane-wrapper')?.getBoundingClientRect().width || this.measuredWrapperWidth;
  }
  getMinimumTreeWidthPercent(wrapperWidth) {
    if (wrapperWidth <= 0) {
      return TREE_WIDTH_MIN_PERCENT;
    }
    return Math.min(TREE_WIDTH_MAX_PERCENT, Math.max(TREE_WIDTH_MIN_PERCENT, TREE_WIDTH_MIN_PX / wrapperWidth * 100));
  }
  clampTreeWidth(width, wrapperWidth) {
    return Math.min(TREE_WIDTH_MAX_PERCENT, Math.max(this.getMinimumTreeWidthPercent(wrapperWidth), width));
  }
  stopTreeResize() {
    this.isResizingTree = false;
    if (typeof window !== 'undefined') {
      window.removeEventListener('pointermove', this.handleSplitterPointerMove);
      window.removeEventListener('pointerup', this.handleSplitterPointerUp);
      window.removeEventListener('pointercancel', this.handleSplitterPointerUp);
    }
  }
  persistTreeWidth() {
    if (typeof window === 'undefined') {
      return;
    }
    createCatCookie(TREE_WIDTH_COOKIE_NAME, this.treeWidthPercent.toFixed(2), CookieConsentCategory.Functional, TREE_WIDTH_COOKIE_EXPIRY_DAYS);
  }
  handleRetry() {
    if (this.selectedPath) {
      this.selectFile(this.selectedPath);
    }
  }

  // Child prop bindings: derive from parent state.

  // The currently selected file node, or null when no selection.
  get selectedNode() {
    if (!this.selectedPath) {
      return null;
    }
    return this.findNodeByPath(this.nodes, this.selectedPath);
  }

  // The selected file's body (from cache), or '' when not yet fetched.
  get selectedFileBody() {
    return this.selectedPath ? this.bodies[this.selectedPath] ?? '' : '';
  }

  // True if the listing has a skillSource (can fetch files).
  get hasSource() {
    return !!this.source;
  }

  // Empty-state message shown when the listing has no resolvable skillSource. Reuses
  // packageFilesEmpty (the same "no files" copy the file tree shows for an empty result),
  // since both cases mean the tab has nothing to browse.
  get packageFilesEmpty() {
    return this.labels?.packageFilesEmpty ?? '';
  }
  get packageFilesHeading() {
    return this.labels?.packageFilesHeading ?? 'Files';
  }
  get twoPaneWrapperClass() {
    return ['two-pane-wrapper', this.isResizingTree ? 'two-pane-wrapper--resizing' : ''].filter(Boolean).join(' ');
  }
  get fileTreePaneClass() {
    return ['file-tree-pane', this.treeCollapsed ? 'file-tree-pane--collapsed' : ''].filter(Boolean).join(' ');
  }
  get splitterClass() {
    return ['pane-splitter', this.treeCollapsed ? 'pane-splitter--collapsed' : ''].filter(Boolean).join(' ');
  }
  get splitterTabIndex() {
    return this.treeCollapsed ? '-1' : '0';
  }
  get splitterMinimum() {
    return Math.round(this.getMinimumTreeWidthPercent(this.getWrapperWidth())).toString();
  }
  get splitterValue() {
    return Math.round(this.clampTreeWidth(this.treeWidthPercent, this.getWrapperWidth())).toString();
  }
  get twoPaneStyle() {
    return `--package-files-tree-width: ${this.treeWidthPercent}%`;
  }

  // True if the tree is empty (no files fetched or fetch failed).
  get treeIsEmpty() {
    return this.nodes.length === 0;
  }

  // True if the selected file is currently being fetched (show spinner).
  get isLoading() {
    return !!this.selectedPath && this.loadingPaths.has(this.selectedPath);
  }
  get hasError() {
    return !!this.selectedPath && this.failedPaths.has(this.selectedPath);
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(SkillPackageFiles, {
  publicProps: {
    listing: {
      config: 3
    }
  },
  wire: {
    labels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: i18n => i18n.skills
        };
      }
    }
  },
  fields: ["_listing", "source", "nodes", "bodies", "selectedPath", "loadingPaths", "failedPaths", "treeCollapsed", "treeWidthPercent", "isResizingTree", "measuredWrapperWidth", "shouldRestoreTreeToggleFocus", "handleSplitterPointerMove", "handleSplitterPointerUp"]
});
const __lwc_component_class_internal = _registerComponent(SkillPackageFiles, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;